import { SelectOptionType, ModalityCodesEnum } from '@octano/global-ui';
import { CoursesFeaturesOptions } from '../../Configurations/academics/interfaces/courses-features.interface';

type Base = {
  id: number;
  name: string;
};

export enum CourseFormMode {
  WATCH = 'WATCH',
  UPDATE = 'UPDATE',
  CREATE = 'CREATE',
}

export type CourseFormData = Partial<{
  schools: SelectOptionType[];
  academicLevels: SelectOptionType[];
  activities: SelectOptionType[];
  modalities: SelectOptionType[];
  studyPlanVersions: SelectOptionType[];
  courseTypes: SelectOptionType[];
  replaceCourseGradeOptions: SelectOptionType[];
  coursesFeatures?: CoursesFeaturesOptions | null;
}>;

export type CourseSyllabus = {
  id: number;
  name: string;
  extension: string;
  url?: string;
};

export type CourseAttendance = {
  minPercentage: number;
  activityTypeId: number;
  /** HU 15624 — Horas totales del componente. Entero >= 0 | null. */
  totalHours?: number | null;
  /** HU 15624 — Horas semanales del componente. Entero >= 0 | null. */
  weeklyHours?: number | null;
};

export type CourseAssistanceRequirements = CourseAttendance & {
  id: number;
  courseId: number;
  createdAt: string;
  updatedAt: string;
};

export type CourseGroupingPick = {
  id: number;
  name: string;
  code: string;
  school: { id: number; name: string } | null;
};

export type Course = {
  id: number;
  shortening: string;
  code: string;
  name: string;
  credits: number;
  totalHours?: number;
  presentialPedagogicalHours?: number | null;
  /** HU 15624 — Horas semanales de la asignatura completa. */
  weeklyHours?: number | null;
  schoolId: number;
  academicLevelId: number;
  modalityId: number | null;
  replaceCourseGradeAllowed: boolean;
  replaceEvaluationGradeAllowed: boolean;
  isNonStructuredAvailable: boolean;
  onlineAttendancePercentage?: number | null;
  /** HU 15746 — Horas totales del componente online (modalidades OL/SP). */
  onlineTotalHours?: number | null;
  /** HU 15746 — Horas semanales del componente online (modalidades OL/SP). */
  onlineWeeklyHours?: number | null;
  semesterCourses?: any[];
  sections?: any[];
  types: Base[];
  assistanceRequirements: CourseAssistanceRequirements[];
  compatibilities: CourseCompatibility[];
  dependsOn: CoursePrerequisite[];
  syllabus: CourseSyllabus | null;
  createdAt: string;
  updatedAt: string;
  courseLearningResults: CourseLearningResult[];
  courseLearningUnits: CourseLearningUnit[];
  courseGroups?: CourseGroupingPick[];
  modality?: {
    id: number;
    name: string;
    deliveryMode: ModalityCodesEnum;
  };
};

export type CourseCompatibility = {
  id: number;
  shortening: string;
  name: string;
  credits: number;
};

export type CoursePrerequisite = {
  id: number;
  shortening: string;
  name: string;
  credits: number;
};

export type CourseLearningResult = {
  id?: number;
  code?: string;
  description?: string;
  indexOnTable?: number;
};

export type CourseLearningUnit = {
  id?: number;
  title?: string;
  hours?: string;
  indexOnTable?: number;
};

export type CourseFormFields = {
  // CourseDetailsSection
  shortening: Course['shortening'];
  code: Course['code'];
  name: Course['name'];
  credits: string;
  isCreditsNotApplicable: boolean;
  totalHours: string | null;
  presentialPedagogicalHours: string | null;
  /** HU 15624 — Horas semanales de la asignatura completa (string en RHF). */
  weeklyHours: string | null;
  school: SelectOptionType | null | undefined;
  academicLevel: SelectOptionType | null | undefined;
  modality: SelectOptionType | null | undefined;
  replaceCourseGradeAllowed: SelectOptionType | null | undefined;
  replaceEvaluationGradeAllowed: SelectOptionType | null | undefined;
  isNonStructuredAvailable: SelectOptionType | null | undefined;
  // CourseTypeSection
  courseTypes: number[];
  // CourseActivityTypeSection
  attendance: CourseAttendance[];
  onlineAttendancePercentage: number | null;
  /**
   * ISS-03 — Horas del componente online. Entero >= 0 | null. Solo se
   * mantienen EN MEMORIA durante la sesión de edición (retención al alternar
   * modalidad); NO se persisten en BD ni se envían en el payload.
   */
  onlineTotalHours: number | null;
  onlineWeeklyHours: number | null;
  // CourseSyllabusSection
  syllabusFile: File | null;
  // CourseCompatibilitiesSection
  compatibilities: CourseCompatibility[];
  // CoursePrerequisitesSection
  prerequisites: CoursePrerequisite[];
  // CourseFooterSection
  learningResults: CourseLearningResult[];
  // CourseLerningUnitSection
  learningUnits: CourseLearningUnit[];
  // CourseGroupingsSection
  courseGroups: CourseGroupingPick[];
};

export enum RetakeAllowedCodes {
  Enabled = 'enabled',
  Disabled = 'disabled',
}
